import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useHistory,
} from "react-router-dom";

import {
  FiArrowLeft,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEdit2,
  FiEye,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiShoppingBag,
  FiSlash,
  FiX,
  FiXCircle,
} from "react-icons/fi";


import {
  fetchPlatformBusinesses,
  setPlatformBusinessFilter,

  selectPlatformBusinesses,
  selectPlatformBusinessPagination,
  selectPlatformBusinessFilters,
  selectPlatformBusinessListLoading,
} from "../../../../../store/platformBusiness/platformBusinessSlice";

import {
  BusinessStatusBadge,
  BusinessFeatureTags,
} from "../business/components";

import "../business/PlatformBusiness.css";

import "./PlatformSubscription.css";


/*
|--------------------------------------------------------------------------
| FORMAT NUMBER
|--------------------------------------------------------------------------
*/

const formatNumber =
  (
    value
  ) => {

    return Number(
      value ||
      0
    )
      .toLocaleString(
        "en-IN"
      );

  };


/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

const formatDate =
  (
    value
  ) => {

    if (
      !value
    ) {

      return "-";

    }


    const date =
      new Date(
        value
      );


    if (
      Number.isNaN(
        date.getTime()
      )
    ) {

      return "-";

    }


    return date
      .toLocaleDateString(
        "en-IN",
        {
          day:
            "2-digit",

          month:
            "short",

          year:
            "numeric",
        }
      );

  };


/*
|--------------------------------------------------------------------------
| BUSINESS ID
|--------------------------------------------------------------------------
*/

const getBusinessId =
  (
    business
  ) => {

    return (
      business?._id ||
      business?.id ||
      null
    );

  };


/*
|--------------------------------------------------------------------------
| SUBSCRIPTION STATUS
|--------------------------------------------------------------------------
*/

const getSubscriptionStatus =
  (
    business
  ) => {

    return String(
      business
        ?.currentSubscription
        ?.status ||
      business
        ?.subscriptionStatus ||
      "NONE"
    )
      .trim()
      .toUpperCase();

  };


/*
|--------------------------------------------------------------------------
| PLAN NAME
|--------------------------------------------------------------------------
*/

const getPlanName =
  (
    business
  ) => {

    return (
      business
        ?.currentSubscription
        ?.planName ||
      business
        ?.currentSubscription
        ?.planSnapshot
        ?.name ||
      business
        ?.currentSubscription
        ?.planCode ||
      ""
    );

  };


/*
|--------------------------------------------------------------------------
| PLAN CODE
|--------------------------------------------------------------------------
*/

const getPlanCode =
  (
    business
  ) => {

    return (
      business
        ?.currentSubscription
        ?.planCode ||
      business
        ?.currentSubscription
        ?.planSnapshot
        ?.code ||
      ""
    );

  };


/*
|--------------------------------------------------------------------------
| SUBSCRIPTION EXPIRY
|--------------------------------------------------------------------------
*/

const getSubscriptionExpiry =
  (
    business
  ) => {

    return (
      business
        ?.currentSubscription
        ?.endsAt ||
      business
        ?.trialEndsAt ||
      null
    );

  };


/*
|--------------------------------------------------------------------------
| PLATFORM SUBSCRIPTION LIST
|--------------------------------------------------------------------------
*/

const PlatformSubscriptionList =
  () => {

    const dispatch =
      useDispatch();


    const history =
      useHistory();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const businesses =
      useSelector(
        selectPlatformBusinesses
      );


    const pagination =
      useSelector(
        selectPlatformBusinessPagination
      );


    const filters =
      useSelector(
        selectPlatformBusinessFilters
      );


    const loading =
      useSelector(
        selectPlatformBusinessListLoading
      );


    /*
    |--------------------------------------------------------------------------
    | LOCAL STATE
    |--------------------------------------------------------------------------
    */

    const [
      searchInput,
      setSearchInput,
    ] =
      useState(
        filters?.search ||
        ""
      );


    const [
      subscriptionFilter,
      setSubscriptionFilter,
    ] =
      useState(
        ""
      );


    /*
    |--------------------------------------------------------------------------
    | LOAD BUSINESSES
    |--------------------------------------------------------------------------
    */

    useEffect(
      () => {

        dispatch(
          fetchPlatformBusinesses({

            page:
              filters?.page ||
              1,

            limit:
              filters?.limit ||
              20,

            search:
              filters?.search ||
              "",

            status:
              filters?.status ||
              "",

          })
        );

      },
      [
        dispatch,
        filters?.page,
        filters?.limit,
        filters?.search,
        filters?.status,
      ]
    );


    /*
    |--------------------------------------------------------------------------
    | FILTER SUBSCRIPTIONS
    |--------------------------------------------------------------------------
    |
    | Currently filtered against businesses loaded on this page.
    |
    |--------------------------------------------------------------------------
    */

    const visibleBusinesses =
      useMemo(
        () => {

          if (
            !subscriptionFilter
          ) {

            return businesses;

          }


          return businesses
            .filter(
              (
                business
              ) => {

                return (
                  getSubscriptionStatus(
                    business
                  ) ===
                  subscriptionFilter
                );

              }
            );

        },
        [
          businesses,
          subscriptionFilter,
        ]
      );


    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const summary =
      useMemo(
        () => {

          return businesses
            .reduce(
              (
                result,
                business
              ) => {

                const status =
                  getSubscriptionStatus(
                    business
                  );


                result.total +=
                  1;


                if (
                  status ===
                  "TRIAL"
                ) {

                  result.trial +=
                    1;

                }


                if (
                  status ===
                  "ACTIVE"
                ) {

                  result.active +=
                    1;

                }


                if (
                  status ===
                  "EXPIRED"
                ) {

                  result.expired +=
                    1;

                }


                if (
                  status ===
                  "SUSPENDED"
                ) {

                  result.suspended +=
                    1;

                }


                return result;

              },
              {
                total:
                  0,

                trial:
                  0,

                active:
                  0,

                expired:
                  0,

                suspended:
                  0,
              }
            );

        },
        [
          businesses,
        ]
      );


    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const currentPage =
      Math.max(
        Number(
          pagination?.page ||
          filters?.page ||
          1
        ),
        1
      );


    const totalPages =
      Math.max(
        Number(
          pagination?.totalPages ||
          1
        ),
        1
      );


    /*
    |--------------------------------------------------------------------------
    | REFRESH
    |--------------------------------------------------------------------------
    */

    const handleRefresh =
      () => {

        dispatch(
          fetchPlatformBusinesses({

            page:
              filters?.page ||
              1,

            limit:
              filters?.limit ||
              20,

            search:
              filters?.search ||
              "",

            status:
              filters?.status ||
              "",

          })
        );

      };


    /*
    |--------------------------------------------------------------------------
    | SEARCH
    |--------------------------------------------------------------------------
    */

    const handleSearch =
      (
        event
      ) => {

        event
          .preventDefault();


        dispatch(
          setPlatformBusinessFilter({
            key:
              "page",

            value:
              1,
          })
        );


        dispatch(
          setPlatformBusinessFilter({
            key:
              "search",

            value:
              searchInput
                .trim(),
          })
        );

      };


    /*
    |--------------------------------------------------------------------------
    | CLEAR SEARCH
    |--------------------------------------------------------------------------
    */

    const handleClearSearch =
      () => {

        setSearchInput(
          ""
        );


        dispatch(
          setPlatformBusinessFilter({
            key:
              "page",

            value:
              1,
          })
        );


        dispatch(
          setPlatformBusinessFilter({
            key:
              "search",

            value:
              "",
          })
        );

      };


    /*
    |--------------------------------------------------------------------------
    | PAGE
    |--------------------------------------------------------------------------
    */

    const handlePageChange =
      (
        page
      ) => {

        const nextPage =
          Math.min(
            Math.max(
              Number(
                page
              ) ||
              1,
              1
            ),
            totalPages
          );


        dispatch(
          setPlatformBusinessFilter({
            key:
              "page",

            value:
              nextPage,
          })
        );

      };


    /*
    |--------------------------------------------------------------------------
    | VIEW DETAILS
    |--------------------------------------------------------------------------
    */

    const handleView =
      (
        business
      ) => {

        const businessId =
          getBusinessId(
            business
          );


        if (
          !businessId
        ) {

          return;

        }


        history.push(
          `/platform/subscriptions/${businessId}`
        );

      };


    /*
    |--------------------------------------------------------------------------
    | MANAGE SUBSCRIPTION
    |--------------------------------------------------------------------------
    |
    | Subscription assignment is handled on the details page using
    | platformTenantSubscriptionSlice.
    |
    |--------------------------------------------------------------------------
    */

    const handleManage =
      (
        business
      ) => {

        const businessId =
          getBusinessId(
            business
          );


        if (
          !businessId
        ) {

          return;

        }


        history.push(
          `/platform/subscriptions/${businessId}`
        );

      };


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (


        <div className="platform-subscription-page">

          {/* ============================================================
              HEADER
          ============================================================ */}

          <header className="platform-subscription-header">

            <div>

              <button
                type="button"
                className="platform-subscription-back"
                onClick={
                  () =>
                    history.push(
                      "/platform/dashboard"
                    )
                }
              >
                <FiArrowLeft />

                Dashboard
              </button>


              <h1>
                Subscription Management
              </h1>


              <p>
                View assigned plans, subscription status,
                expiry dates and tenant platform access.
              </p>

            </div>


            <button
              type="button"
              className="platform-subscription-refresh"
              onClick={
                handleRefresh
              }
              disabled={
                loading
              }
            >

              <FiRefreshCw
                className={
                  loading
                    ?
                    "platform-subscription-spin"
                    :
                    ""
                }
              />

              Refresh

            </button>

          </header>


          {/* ============================================================
              SUMMARY
          ============================================================ */}

          <section className="platform-subscription-summary">

            <article>

              <div>
                <FiBriefcase />
              </div>

              <span>
                Businesses
              </span>

              <strong>
                {
                  formatNumber(
                    pagination
                      ?.totalRecords ??
                    pagination
                      ?.total ??
                    businesses.length
                  )
                }
              </strong>

            </article>


            <article>

              <div>
                <FiClock />
              </div>

              <span>
                Trial On Page
              </span>

              <strong>
                {
                  summary.trial
                }
              </strong>

            </article>


            <article>

              <div>
                <FiCheckCircle />
              </div>

              <span>
                Active On Page
              </span>

              <strong>
                {
                  summary.active
                }
              </strong>

            </article>


            <article>

              <div>
                <FiXCircle />
              </div>

              <span>
                Expired On Page
              </span>

              <strong>
                {
                  summary.expired
                }
              </strong>

            </article>


            <article>

              <div>
                <FiSlash />
              </div>

              <span>
                Suspended On Page
              </span>

              <strong>
                {
                  summary.suspended
                }
              </strong>

            </article>

          </section>


          {/* ============================================================
              FILTER
          ============================================================ */}

          <section className="platform-subscription-toolbar">

            <form
              className="platform-subscription-search"
              onSubmit={
                handleSearch
              }
            >

              <FiSearch />


              <input
                type="text"
                value={
                  searchInput
                }
                onChange={
                  (
                    event
                  ) =>
                    setSearchInput(
                      event
                        .target
                        .value
                    )
                }
                placeholder="Search business name, code, mobile or email..."
              />


              {
                searchInput
                  ?
                  (
                    <button
                      type="button"
                      className="platform-subscription-clear"
                      onClick={
                        handleClearSearch
                      }
                    >
                      <FiX />
                    </button>
                  )
                  :
                  null
              }


              <button
                type="submit"
              >
                Search
              </button>

            </form>


            <div className="platform-subscription-filter">

              <FiFilter />


              <select
                value={
                  subscriptionFilter
                }
                onChange={
                  (
                    event
                  ) =>
                    setSubscriptionFilter(
                      event
                        .target
                        .value
                    )
                }
              >

                <option value="">
                  All Subscriptions
                </option>

                <option value="TRIAL">
                  Trial
                </option>

                <option value="ACTIVE">
                  Active
                </option>

                <option value="EXPIRED">
                  Expired
                </option>

                <option value="SUSPENDED">
                  Suspended
                </option>

              </select>

            </div>

          </section>


          {/* ============================================================
              TABLE
          ============================================================ */}

          <section className="platform-subscription-table-card">

            <div className="platform-subscription-table-wrapper">

              <table className="platform-subscription-table">

                <thead>

                  <tr>

                    <th>
                      Business
                    </th>

                    <th>
                      Code
                    </th>

                    <th>
                      Business Status
                    </th>

                    <th>
                      Plan
                    </th>

                    <th>
                      Subscription
                    </th>

                    <th>
                      Billing
                    </th>

                    <th>
                      Expires
                    </th>

                    <th>
                      Features
                    </th>

                    <th>
                      Actions
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {
                    loading &&
                    businesses.length ===
                      0
                      ?
                      (
                        <tr>

                          <td colSpan="9">

                            <div className="platform-subscription-state">

                              <div className="platform-business-loader" />

                              <strong>
                                Loading subscriptions...
                              </strong>

                            </div>

                          </td>

                        </tr>
                      )
                      :
                      visibleBusinesses.length >
                      0
                        ?
                        visibleBusinesses.map(
                          (
                            business
                          ) => {

                            const businessId =
                              getBusinessId(
                                business
                              );


                            const currentSubscription =
                              business
                                ?.currentSubscription ||
                              null;


                            const planName =
                              getPlanName(
                                business
                              );


                            const planCode =
                              getPlanCode(
                                business
                              );


                            const subscriptionStatus =
                              getSubscriptionStatus(
                                business
                              );


                            const expiry =
                              getSubscriptionExpiry(
                                business
                              );


                            return (

                              <tr
                                key={
                                  businessId
                                }
                              >

                                {/* BUSINESS */}

                                <td>

                                  <div className="platform-subscription-business">

                                    <div>

                                      {
                                        String(
                                          business
                                            ?.name ||
                                          "B"
                                        )
                                          .charAt(
                                            0
                                          )
                                          .toUpperCase()
                                      }

                                    </div>


                                    <section>

                                      <strong>
                                        {
                                          business
                                            ?.name ||
                                          "-"
                                        }
                                      </strong>


                                      <span>
                                        {
                                          business
                                            ?.email ||
                                          business
                                            ?.mobile ||
                                          "-"
                                        }
                                      </span>

                                    </section>

                                  </div>

                                </td>


                                {/* CODE */}

                                <td>

                                  <span className="platform-subscription-code">

                                    {
                                      business
                                        ?.code ||
                                      "-"
                                    }

                                  </span>

                                </td>


                                {/* BUSINESS STATUS */}

                                <td>

                                  <BusinessStatusBadge
                                    status={
                                      business
                                        ?.status
                                    }
                                  />

                                </td>


                                {/* PLAN */}

                                <td>

                                  <div className="platform-subscription-plan-cell">

                                    <strong>
                                      {
                                        planName ||
                                        "No Plan"
                                      }
                                    </strong>


                                    {
                                      planCode
                                        ?
                                        (
                                          <span className="platform-subscription-plan-code">
                                            {
                                              planCode
                                            }
                                          </span>
                                        )
                                        :
                                        null
                                    }

                                  </div>

                                </td>


                                {/* SUBSCRIPTION STATUS */}

                                <td>

                                  <BusinessStatusBadge
                                    status={
                                      subscriptionStatus
                                    }
                                  />

                                </td>


                                {/* BILLING */}

                                <td>

                                  <span className="platform-subscription-billing-cycle">

                                    {
                                      currentSubscription
                                        ?.billingCycle ||
                                      "-"
                                    }

                                  </span>

                                </td>


                                {/* EXPIRY */}

                                <td>

                                  <div className="platform-subscription-date">

                                    <FiCalendar />

                                    {
                                      formatDate(
                                        expiry
                                      )
                                    }

                                  </div>

                                </td>


                                {/* FEATURES */}

                                <td>

                                  <BusinessFeatureTags
                                    storefrontEnabled={
                                      currentSubscription
                                        ?.features
                                        ?.storefrontEnabled ??
                                      business
                                        ?.storefrontEnabled
                                    }

                                    marketplaceEnabled={
                                      currentSubscription
                                        ?.features
                                        ?.marketplaceEnabled ??
                                      business
                                        ?.marketplaceEnabled
                                    }
                                  />

                                </td>


                                {/* ACTIONS */}

                                <td>

                                  <div className="platform-subscription-actions">

                                    <button
                                      type="button"
                                      title="View Subscription"
                                      onClick={
                                        () =>
                                          handleView(
                                            business
                                          )
                                      }
                                    >
                                      <FiEye />
                                    </button>


                                    <button
                                      type="button"
                                      title="Manage Subscription"
                                      onClick={
                                        () =>
                                          handleManage(
                                            business
                                          )
                                      }
                                    >
                                      <FiEdit2 />
                                    </button>

                                  </div>

                                </td>

                              </tr>

                            );

                          }
                        )
                        :
                        (
                          <tr>

                            <td colSpan="9">

                              <div className="platform-subscription-empty">

                                <FiShoppingBag />

                                <strong>
                                  No subscriptions found
                                </strong>

                                <span>
                                  Try changing your filters or search.
                                </span>

                              </div>

                            </td>

                          </tr>
                        )
                  }

                </tbody>

              </table>

            </div>


            {/* ============================================================
                PAGINATION
            ============================================================ */}

            <div className="platform-subscription-pagination">

              <span>

                Page{" "}

                <strong>
                  {
                    currentPage
                  }
                </strong>

                {" "}of{" "}

                <strong>
                  {
                    totalPages
                  }
                </strong>

              </span>


              <div>

                <button
                  type="button"
                  disabled={
                    loading ||
                    currentPage <=
                      1
                  }
                  onClick={
                    () =>
                      handlePageChange(
                        currentPage -
                        1
                      )
                  }
                >
                  Previous
                </button>


                <span>
                  {
                    currentPage
                  }
                </span>


                <button
                  type="button"
                  disabled={
                    loading ||
                    currentPage >=
                      totalPages
                  }
                  onClick={
                    () =>
                      handlePageChange(
                        currentPage +
                        1
                      )
                  }
                >
                  Next
                </button>

              </div>

            </div>

          </section>

        </div>

    );

  };


export default
  PlatformSubscriptionList;